// React and routing imports
import {useMemo} from 'react'
import {useNavigate} from 'react-router-dom'

// Component-specific styles
import './promotionList.scss'

// Custom Organism Components
import {CustomDataTable, FileUploadWithModal, UploadHistoryTableModal, UploadMediaModal} from '@/components/organisms'

// Helpers
import {getCellDataMappedCols} from '@/helpers/dataTableCellDataMapper'

// Custom Hooks
import {usePromotionList} from '@/hooks/usePromotionList'

// Constants
import {Constants, DEFAULT_TABLE_PAGE_SIZE} from '@/utils/constants/commonConstants'
import {ROUTES} from '@/utils/constants/routesConstants'
import {MAIN_COLUMNS, SUB_COLUMNS, cellDataMapperObj} from './PromotionListConstants'

/**
 * PromotionsList component displays a sortable, filterable, and paginated list of promotions.
 */
export const PromotionsList = () => {
  const navigate = useNavigate()
  const {
    promotionListData,
    promotionListColConfigData,
    setSearchArry,
    isLoading,
    createSearchArray,
    resetAllBtnClicked,
    formData,
    setFormData,
    downloadPromoTemplate,
    downloadPromotionFile,
    getPromotionFileUploadErrors,
    downloadUploadError,
    uploadHistoryData,
    toggleMultipleImagesModal,
    isMultipleImagesModal,
    handleCopyClick,
    handleResetAll,
    handleSorting,
    handlePageIndexChange,
    isSelectAll,
    setSelectAll,
    isModal,
    toggleModal,
    openUploadHistory,
    setOpenUploadHistory,
    setSelectedRows,
    tableActions,
  } = usePromotionList()

  // Get cell data mapped columns
  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj(navigate, handleCopyClick), promotionListColConfigData)

  const transformedColumns = useMemo(
    () =>
      cellDataMappedCols.map(col => {
        const baseConfig = {
          name_of_db_column: col.name_of_db_column,
          label_of_column: col.columnFilterPlaceholder,
          filterVariant: col.filterVariant || 'text',
        }

        // Conditional enhancements
        if (col.name_of_db_column === 'name') {
          return {
            ...baseConfig,
            isClickable: true,
            onCellClick: (value, row) => {
              if (value && row?.id) {
                navigate(ROUTES.PROMOTION_DETAIL.replace(':id', row.id))
              }
            },
          }
        }

        if (col.name_of_db_column === 'status') {
          return {
            ...baseConfig,
            isClickable: true,
            isIconShow: true,
            iconTooltip: 'Duplicate',
          }
        }

        return baseConfig
      }),
    [cellDataMappedCols, navigate]
  )

  // Prepare columns for UploadHistoryTableModal
  const columns = MAIN_COLUMNS(downloadPromotionFile, downloadUploadError)

  return (
    <div className="product-admin-promotions-list">
      <CustomDataTable
        columns={transformedColumns}
        records={promotionListData?.data || []}
        setSearchArry={setSearchArry}
        handleSorting={handleSorting}
        setPageIndex={handlePageIndexChange}
        pageSize={DEFAULT_TABLE_PAGE_SIZE}
        totalRecords={promotionListData?.totalRecords}
        onResetAll={() => {
          handleResetAll()
          resetAllBtnClicked()
        }}
        onRowSelect={selectedRows => setSelectedRows(selectedRows)}
        tableHeader="Promotions List"
        isLoading={isLoading}
        actions={tableActions}
        id="id"
        showActions={true}
        copyIconClick={row => handleCopyClick(row?.id, row?.promotionType)}
        isSelectAll={isSelectAll}
        setSelectAll={setSelectAll}
        defaultSortConfig={{key: 'activeStartDate', direction: 'desc'}}
      />
      <FileUploadWithModal
        openUpload={isModal}
        setOpenUpload={toggleModal}
        setFormData={setFormData}
        formData={formData}
        title="Bulk Upload Promotions"
        showTertiaryButton={true}
        uploadFor={'product'}
        isCsvValidationRequired={false}
        isTemplateUpload={true}
        label={'Upload Bulk Promotion file'}
        toastMessage={'File has successfully been uploaded and the status can be checked in Upload History'}
        tertiaryButtonText={'Download Template'}
        onClickTertiaryButton={downloadPromoTemplate}
        showTertiaryDownloadButton={true}
        acceptedExtensions={Constants.Xlsx}
      />
      <UploadHistoryTableModal
          openUploadHistory={openUploadHistory}
          setOpenUploadHistory={setOpenUploadHistory}
          mainData={uploadHistoryData?.data || []}
          totalRecords={uploadHistoryData?.totalRecords}
          mainColumns={columns}
          subColumns={SUB_COLUMNS}
          fetchSubRows={getPromotionFileUploadErrors}
          isDark={true}
          setPageIndex={0}
          text={'Access original files under File Path column, or download error reports by clicking Failed to resolve issues.'}
        />

      <UploadMediaModal
        openUpload={isMultipleImagesModal}
        setOpenUpload={toggleMultipleImagesModal}
        title="Upload Media"
        showTertiaryButton={true}
        formData={formData}
        setFormData={setFormData}
        entityType="promotion"
      />
    </div>
  )
}
